import { NetworkStatus, useQuery } from "@apollo/client";
import { createContext, ReactNode } from "react";
import { Outlet } from "react-router-dom";

import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import { AuthorizationScheme, Query } from "types/generated";
import { isSaasDistribution } from "utils/distribution";
import { AccountContext } from "views/AccountWrapper";

import { GET_ACCOUNT_SETTINGS } from "./gql";
import { SettingsContextValue, UserManagementActivationStatus } from "./types";

const isSaas = isSaasDistribution();

export const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);
SettingsContext.displayName = "SettingsContext";

type GetAccountSettingsGqlBase = {
  authorizationScheme: Query["authorizationScheme"];
  seats: Query["seats"];
};

type GetAccountSettingsGqlAdmin = GetAccountSettingsGqlBase & {
  securityEmail?: Query["securityEmail"];
};

type GetAccountSettingsGqlNonAdmin = GetAccountSettingsGqlBase & {
  securityEmail: undefined;
};

type GetAccountSettingsGql = GetAccountSettingsGqlAdmin | GetAccountSettingsGqlNonAdmin;

export const SettingsContextProvider = ({ children }: { children?: ReactNode }) => {
  const { viewer } = useTypedContext(AccountContext);

  const { loading, data, error, networkStatus } = useQuery<GetAccountSettingsGql>(
    GET_ACCOUNT_SETTINGS,
    {
      variables: {
        isSaas,
        isAdmin: viewer.admin,
      },
    }
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  const isLoading = loading && networkStatus !== NetworkStatus.refetch;

  if (isLoading || !data) {
    return <PageLoading />;
  }

  const activationStatus =
    data?.authorizationScheme === AuthorizationScheme.ManagedUsers
      ? UserManagementActivationStatus.ACTIVE
      : UserManagementActivationStatus.INACTIVE;

  const settingsContextValue = {
    activationStatus,
    seats: data.seats,
    securityEmail: data.securityEmail,
  };

  return (
    <SettingsContext.Provider value={settingsContextValue}>
      {children || <Outlet />}
    </SettingsContext.Provider>
  );
};
